import { ToastId, useToast } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';

import { AppProviders } from './components/AppProviders/AppProviders';
import AuthGuard from './components/Auth/AuthGuard';
import { initGA } from './features/analytic/analytics';
import { RouteObserver } from './features/analytic/RouteObserver';
import LiveMeetingPermissions from './features/meeting/LiveMeetingPermissions';
import MeetingStatsViewContextProvider from './features/meeting-stats/context/MeetingStatsViewContext';
import AccessLinkEnterCodePage from './pages/AccessLinkEnterCodePage/AccessLinkEnterCodePage';
import AdminPage from './pages/admin/AdminPage';
import { VideoCallWrapper } from './pages/caseCluster/VideoCallWrapper';
import { GlobalGuard } from './pages/globalGuard/GlobalGuard';
import HomeView from './pages/home/HomeView';
import { MeetingStatsView } from './pages/meeting-stats/MeetingStatsView/MeetingStatsView';
import EmptyPageWithTitle from './pages/page404/EmptyPageWithTitle.tsx';
import PreviewCasePermissions from './pages/previewCase/PreviewCasePermissions';
import { RegisterPage } from './pages/RegisterPage/RegisterPage';
import AdminTestPage from './pages/test/AdminTestPage';
import TestPage from './pages/test/TestPage';
import { UseAccessLinkPage } from './pages/UseAccessLinkPage/UseAccessLinkPage';
import AdminRoute from './routes/AdminRoute';
import { ROUTES } from './routes/Routes';
import { ErrorFallback } from './ui/ErrorFallback/ErrorFallback';
import useNetworkConnection from './utils/helpers/hooks/useNetwork';
import { reloadApp } from './utils/reloadApp';

const App = () => {
  const { online } = useNetworkConnection();
  const { t } = useTranslation();
  const toast = useToast();
  const offlineNotificationToastRef = useRef<ToastId | null>(null);

  // Initialize Google Analytics once when app loads
  useEffect(() => {
    initGA();
  }, []);

  if (!online) {
    offlineNotificationToastRef.current = toast({
      status: 'warning',
      title: t('warning.noNetwork'),
      size: 'md',
      position: 'top',
      isClosable: false,
      duration: null,
    });
  } else {
    if (offlineNotificationToastRef.current) {
      toast.close(offlineNotificationToastRef.current);
    }
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        console.error('Error boundary reset');
        reloadApp();
      }}
    >
      <AppProviders>
        <GlobalGuard>
          <RouteObserver />
          <Routes>
            <Route path={ROUTES.ACCESS_LINK} element={<AccessLinkEnterCodePage />} />
            <Route path={`${ROUTES.ACCESS_LINK}/:accessCode`} element={<UseAccessLinkPage />} />
            <Route path={`${ROUTES.REGISTER}/:accessCode`} element={<RegisterPage />} />
            <Route
              path={`${ROUTES.ADMIN}/*`}
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN_TEST}`}
              element={
                <AdminRoute>
                  <AdminTestPage />
                </AdminRoute>
              }
            />
            <Route path="/test" element={<TestPage />} />
            <Route
              path={`${ROUTES.LIVE_MEETING}/:meetingId`}
              element={
                <AuthGuard>
                  <VideoCallWrapper>
                    <LiveMeetingPermissions />
                  </VideoCallWrapper>
                </AuthGuard>
              }
            />

            <Route
              path={`${ROUTES.PREVIEW_CASE}/:patientCaseIds`}
              element={
                <AuthGuard>
                  <PreviewCasePermissions />
                </AuthGuard>
              }
            />
            <Route
              path="/"
              element={
                <AuthGuard>
                  <HomeView />
                </AuthGuard>
              }
            />
            <Route
              path={`${ROUTES.MEETING_STATS}/:meetingID`}
              element={
                <AuthGuard>
                  <MeetingStatsViewContextProvider>
                    <MeetingStatsView />
                  </MeetingStatsViewContextProvider>
                </AuthGuard>
              }
            />
            <Route
              path="*"
              element={<EmptyPageWithTitle title="Sorry..." subtitle="The page was not found." />}
            />
          </Routes>
        </GlobalGuard>
      </AppProviders>
    </ErrorBoundary>
  );
};
export default App;
